import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Camera } from "@capacitor/camera";
import { CameraResultType, CameraSource } from "@capacitor/camera";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ImageCropper } from "@/components/ImageCropper";
import { ImageComparisonSlider } from "@/components/ui/image-comparison-slider-horizontal";
import { ShareButtons } from "@/components/ShareButtons";
import { MovemberDonationModal } from "@/components/MovemberDonationModal";
import { compressImageBlob } from "@/lib/imageUtils";
import { revokeTrackedBlobUrl } from "@/lib/memoryUtils";
import { getSliderImageUrl, getPreviewImageUrl } from "@/lib/imageOptimization";
import { addWatermark } from "@/lib/watermarkUtils";
import { LumaSpin } from "@/components/ui/luma-spin";
import { Camera as CameraIcon, Upload, ArrowLeft, Heart } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";

// Import Movember-specific mustache images
import chevronImg from "@/assets/movember/chevron.jpg";
import handlebarImg from "@/assets/movember/handlebar.jpg";
import pencilImg from "@/assets/movember/pencil.jpg";
import walrusImg from "@/assets/movember/walrus.jpg";
import horseshoeImg from "@/assets/movember/horseshoe.jpg";
import painterBrushImg from "@/assets/movember/painter-brush.jpg";
import englishImg from "@/assets/movember/english.jpg";
import fuManchuImg from "@/assets/movember/fu-manchu.jpg";
interface Mustache {
  id: string;
  name: string;
  image_url: string;
}

// Movember-exclusive mustache styles
const MOVEMBER_MUSTACHES: Mustache[] = [{
  id: "chevron",
  name: "The Chevron",
  image_url: chevronImg
}, {
  id: "handlebar",
  name: "The Handlebar",
  image_url: handlebarImg
}, {
  id: "pencil",
  name: "The Pencil",
  image_url: pencilImg
}, {
  id: "walrus",
  name: "The Walrus",
  image_url: walrusImg
}, {
  id: "horseshoe",
  name: "The Horseshoe",
  image_url: horseshoeImg
}, {
  id: "painter-brush",
  name: "The Painter Brush",
  image_url: painterBrushImg
}, {
  id: "english",
  name: "The English",
  image_url: englishImg
}, {
  id: "fu-manchu",
  name: "The Fu Manchu",
  image_url: fuManchuImg
}];
const Movember = () => {
  const {
    user,
    loading: authLoading
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const [tempPhoto, setTempPhoto] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedMustache, setSelectedMustache] = useState<Mustache | null>(null);
  const [resultPhoto, setResultPhoto] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const {
    toast
  } = useToast();
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);
  useEffect(() => {
    const donation = searchParams.get('donation');
    if (donation === 'success') {
      toast({
        title: "Thank you! 🎗️",
        description: "Your donation helps support men's health initiatives."
      });
    } else if (donation === 'cancelled') {
      toast({
        title: "Donation cancelled",
        description: "You can still generate mustaches for free!"
      });
    }
  }, [searchParams, toast]);
  useEffect(() => {
    return () => {
      if (tempPhoto?.startsWith('blob:')) {
        revokeTrackedBlobUrl(tempPhoto);
      }
    };
  }, [tempPhoto]);
  useEffect(() => {
    return () => {
      if (userPhoto?.startsWith('blob:')) {
        revokeTrackedBlobUrl(userPhoto);
      }
    };
  }, [userPhoto]);
  const handlePhotoCapture = async (source: CameraSource) => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: source
      });
      if (image.dataUrl) {
        setTempPhoto(image.dataUrl);
        setShowCropper(true);
      }
    } catch (error) {
      console.error('Error capturing photo:', error);
      toast({
        title: "Error",
        description: "Failed to capture photo",
        variant: "destructive"
      });
    }
  };
  const handleCropComplete = async (croppedImage: string) => {
    if (!user) {
      console.error('No user found');
      return;
    }
    try {
      const blob = await fetch(croppedImage).then(r => r.blob());
      const compressedBlob = await compressImageBlob(blob, 0.8, 1024);

      // Revoke the cropped image URL immediately
      URL.revokeObjectURL(croppedImage);
      const timestamp = Date.now();
      const path = `${user.id}/temp_${timestamp}.jpg`;
      console.log('Uploading to storage:', path);
      const {
        error: uploadError
      } = await supabase.storage.from('user-photos').upload(path, compressedBlob, {
        contentType: 'image/jpeg'
      });
      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw uploadError;
      }

      // Store only the storage URL in state, not blob
      const {
        data: {
          publicUrl
        }
      } = supabase.storage.from('user-photos').getPublicUrl(path);
      console.log('Public URL:', publicUrl);
      console.log('URL starts with https:', publicUrl.startsWith('https://'));
      if (userPhoto?.startsWith('blob:')) {
        revokeTrackedBlobUrl(userPhoto);
      }
      setUserPhoto(publicUrl);
      setResultPhoto(null);
      setShowCropper(false);
      setTempPhoto(null);
      toast({
        title: "Photo uploaded",
        description: "Now select a mustache style!"
      });
    } catch (error) {
      console.error('Error processing photo:', error);
      toast({
        title: "Error",
        description: "Failed to process photo",
        variant: "destructive"
      });
    }
  };
  const applyMustache = async () => {
    if (!userPhoto || !selectedMustache || !user) {
      toast({
        title: "Missing information",
        description: "Please upload a photo and select a mustache",
        variant: "destructive"
      });
      return;
    }
    console.log('=== Starting mustache application ===');
    console.log('User photo URL:', userPhoto);
    console.log('Starts with https:', userPhoto.startsWith('https://'));
    console.log('Starts with http:', userPhoto.startsWith('http://'));
    console.log('Starts with blob:', userPhoto.startsWith('blob:'));
    console.log('Mustache:', selectedMustache.name);
    if (resultPhoto && resultPhoto.startsWith('blob:')) {
      revokeTrackedBlobUrl(resultPhoto);
    }
    setResultPhoto(null);
    setLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('apply-hairstyle', {
        body: {
          userPhotoUrl: userPhoto,
          hairstyleDescription: selectedMustache.name,
          styleType: 'facial_hair'
        }
      });
      console.log('=== Apply mustache response ===');
      console.log('Data:', data);
      console.log('Error:', error);
      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }
      if (data?.imageUrl) {
        // Add watermark to the result
        try {
          const watermarkedUrl = await addWatermark(data.imageUrl, 'cutpreviews.com/movember');
          setResultPhoto(watermarkedUrl);
        } catch (watermarkError) {
          console.error('Failed to add watermark:', watermarkError);
          // Fall back to non-watermarked image
          setResultPhoto(data.imageUrl);
        }

        // Show donation modal after successful generation
        setShowDonationModal(true);
        toast({
          title: "Mustache applied! 🥸",
          description: "Looking great for Movember!"
        });
      }
    } catch (error: any) {
      console.error('Error applying mustache:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to apply mustache",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  return <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="border-b bg-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="h-8 w-8 text-black" />
            <h1 className="text-2xl font-anton uppercase tracking-tight text-neutral-950">Movember</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-sm font-semibold uppercase tracking-wide text-neutral-950">
            Back to App
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-b">
        <div className="max-w-7xl mx-auto px-4 py-12 bg-white">
          <div className="max-w-3xl">
            <h2 className="text-5xl md:text-6xl font-anton uppercase leading-tight mb-6 text-neutral-950">
              Grow a mustache.<br />
              Raise funds.<br />
              Save lives.
            </h2>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Join the global movement tackling mental health, suicide, prostate and testicular cancer.
              Try on mustaches with AI and support the cause.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full">
        {/* Preview Area */}
        <div className="p-6">
          <div className="max-w-md mx-auto">
            <Card className="relative aspect-square overflow-hidden bg-gray-100 shadow-xl">
              {loading && <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
                  <div className="flex flex-col items-center gap-4">
                    <div className="scale-150">
                      <LumaSpin />
                    </div>
                    <p className="text-sm text-white font-semibold">Growing your mustache...</p>
                  </div>
                </div>}

              {!userPhoto && !resultPhoto && <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center space-y-4 p-8">
                    <Upload className="h-16 w-16 mx-auto text-gray-400" />
                    <div>
                      <p className="font-bold text-lg text-gray-900 mb-2">Upload Your Photo</p>
                      <p className="text-sm text-gray-600">Try on a mustache and support Movember</p>
                    </div>
                  </div>
                </div>}

              {userPhoto && !resultPhoto && <img src={getPreviewImageUrl(userPhoto)} alt="Your photo" className="w-full h-full object-cover" />}

              {resultPhoto && userPhoto && <ImageComparisonSlider leftImage={getSliderImageUrl(userPhoto)} rightImage={getSliderImageUrl(resultPhoto)} altLeft="Original" altRight={selectedMustache?.name || "Result"} />}
            </Card>

            {/* Action Buttons */}
            <div className="mt-6 space-y-3">
              <div className="flex gap-3">
                <Button onClick={() => handlePhotoCapture(CameraSource.Camera)} className="flex-1 h-14 bg-black hover:bg-gray-800 text-white font-bold uppercase tracking-wide">
                  <CameraIcon className="h-5 w-5 mr-2" />
                  Camera
                </Button>
                <Button onClick={() => handlePhotoCapture(CameraSource.Photos)} className="flex-1 h-14 bg-black hover:bg-gray-800 text-white font-bold uppercase tracking-wide" variant="outline">
                  <Upload className="h-5 w-5 mr-2" />
                  Upload
                </Button>
              </div>

              {resultPhoto && <ShareButtons imageUrl={resultPhoto} hairstyleName={selectedMustache?.name || "mustache"} />}

              {selectedMustache && userPhoto && !loading && <Button onClick={applyMustache} size="lg" className="w-full h-14 text-base font-bold uppercase tracking-wide bg-black hover:bg-gray-800 text-zinc-50">
                  Try {selectedMustache.name}
                </Button>}
            </div>
          </div>
        </div>

        {/* Mustache Carousel */}
        <div className="border-t bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <h3 className="text-xl font-anton uppercase mb-4 tracking-tight text-neutral-950">
              Choose Your Mo Style
            </h3>
          </div>
          
          <ScrollArea className="w-full">
            <div className="flex gap-3 px-4 pb-6 pt-4">
              {MOVEMBER_MUSTACHES.map(mustache => <Card key={mustache.id} onClick={() => setSelectedMustache(mustache)} className={`flex-shrink-0 cursor-pointer transition-all duration-200 hover:scale-105 active:scale-95 relative border-2 ${selectedMustache?.id === mustache.id ? 'ring-4 ring-black ring-offset-2 shadow-xl scale-105 border-black' : 'hover:shadow-lg border-gray-200'}`}>
                  <div className="w-24 h-32 overflow-hidden rounded-t-lg relative bg-white">
                    <img src={mustache.image_url} alt={mustache.name} className="w-full h-full object-cover object-top" loading="lazy" />
                    {selectedMustache?.id === mustache.id && <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-black border-2 border-white animate-pulse shadow-lg" />}
                  </div>
                  <div className="w-24 p-2 text-center bg-white">
                    <p className="text-[10px] font-bold uppercase tracking-wide line-clamp-2 leading-tight text-gray-900">
                      {mustache.name}
                    </p>
                  </div>
                </Card>)}
            </div>
            <ScrollBar orientation="horizontal" className="h-2" />
          </ScrollArea>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white py-8 border-t">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm font-semibold uppercase tracking-wide mb-2">
            Supporting Movember Foundation
          </p>
          <p className="text-xs text-gray-400">
            All donations support men's health initiatives worldwide
          </p>
        </div>
      </footer>

      {/* Image Cropper */}
      {showCropper && tempPhoto && <ImageCropper image={tempPhoto} onCropComplete={handleCropComplete} onCancel={() => {
      setShowCropper(false);
      setTempPhoto(null);
    }} />}

      {/* Donation Modal */}
      <MovemberDonationModal open={showDonationModal} onClose={() => setShowDonationModal(false)} onDonationComplete={() => {
      // Modal closes automatically after donation
    }} />
    </div>;
};
export default Movember;